import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AdminContext = createContext();
const ADMIN_STORAGE_KEY = 'sdny_admin_auth';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Role constants matching backend
export const ADMIN_ROLES = {
  SUPER_ADMIN: 0,
  ADMIN: 1,
  OPERATIONS: 2,
  SUPPORT: 3
};

export const ROLE_NAMES = {
  0: 'Super Admin',
  1: 'Admin',
  2: 'Operations',
  3: 'Support'
};

// Permission check helper
export const hasPermission = (role, resource, action = null) => {
  const permissions = {
    [ADMIN_ROLES.SUPER_ADMIN]: { all: true },
    [ADMIN_ROLES.ADMIN]: {
      dashboard: true,
      orders: { read: true, write: true, delete: false },
      products: { read: true, write: true, delete: true },
      diamonds: { read: true, write: true, lock: true },
      customers: { read: true, write: true },
      payments: { read: true, refund: true },
      shipping: { read: true, write: true },
      reports: true,
      settings: { read: true, write: false },
      audit_logs: true,
    },
    [ADMIN_ROLES.OPERATIONS]: {
      dashboard: true,
      orders: { read: true, write: true },
      products: { read: true, write: true },
      diamonds: { read: true, write: true, lock: true },
      customers: { read: true },
      payments: { read: true },
      shipping: { read: true, write: true },
      reports: true,
    },
    [ADMIN_ROLES.SUPPORT]: {
      dashboard: true,
      orders: { read: true },
      products: { read: true },
      diamonds: { read: true },
      customers: { read: true, write: true },
      payments: { read: true },
      shipping: { read: true },
    },
  };

  const rolePerms = permissions[role];
  if (!rolePerms) return false;
  if (rolePerms.all) return true;

  const resourcePerm = rolePerms[resource];
  if (resourcePerm === undefined) return false;
  if (typeof resourcePerm === 'boolean') return resourcePerm;
  if (action) return resourcePerm[action] || false;
  return Object.values(resourcePerm).some(v => v);
};

export function AdminProvider({ children }) {
  // Initialize state synchronously from localStorage to avoid race conditions
  const [admin, setAdmin] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.admin || null;
      }
    } catch (e) {
      console.warn('Failed to load admin from localStorage', e);
    }
    return null;
  });
  
  const [token, setToken] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.token || null;
      }
    } catch (e) {
      console.warn('Failed to load token from localStorage', e);
    }
    return null;
  });
  
  // Loading is now false by default since we initialize synchronously
  const [loading, setLoading] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (token && !tokenValidated) {
        try {
          const response = await axios.get(`${BACKEND_URL}/admin/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            setAdmin(response.data.admin);
            setTokenValidated(true);
          } else {
            // Invalid token
            console.log('Token validation failed - clearing auth');
            setAdmin(null);
            setToken(null);
            localStorage.removeItem(ADMIN_STORAGE_KEY);
          }
        } catch (error) {
          // Token is invalid or expired
          console.log('Token validation error - clearing auth', error.response?.status);
          if (error.response?.status === 401) {
            setAdmin(null);
            setToken(null);
            localStorage.removeItem(ADMIN_STORAGE_KEY);
            
            // Redirect to login if on admin page
            if (window.location.pathname.startsWith('/admin') && 
                !window.location.pathname.includes('/login')) {
              window.location.href = '/admin/login';
            }
          }
        }
      }
    };
    
    validateToken();
  }, [token, tokenValidated]);

  // Save to localStorage when state changes
  useEffect(() => {
    if (!loading) {
      if (admin && token) {
        localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify({ admin, token }));
      } else {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
      }
    }
  }, [admin, token, loading]);

  // Create axios instance with auth header - initialized with token
  const adminAxios = React.useMemo(() => {
    const instance = axios.create({
      baseURL: BACKEND_URL,
    });
    // Set initial token if available
    if (token) {
      instance.defaults.headers.Authorization = `Bearer ${token}`;
    }
    return instance;
  }, []); // Empty deps - create once, update via effect

  // Update headers and interceptors when token changes
  useEffect(() => {
    if (token) {
      adminAxios.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete adminAxios.defaults.headers.Authorization;
    }

    // Add response interceptor for handling 401 errors
    const interceptorId = adminAxios.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl = error.config?.url || '';
        const isAdminProtectedRequest =
          requestUrl.includes('/admin') ||
          requestUrl.includes('/appraisal/admin') ||
          requestUrl.includes('/milestone-payments/admin');

        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401 && isAdminProtectedRequest) {
          console.log('401 Error detected - redirecting to login');
          
          // Clear auth state
          setAdmin(null);
          setToken(null);
          localStorage.removeItem(ADMIN_STORAGE_KEY);
          
          // Show error message
          const errorMsg = error.response?.data?.detail || 'Session expired. Please login again.';
          toast.error(errorMsg);
          
          // Force redirect to login page
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/admin') && !currentPath.includes('/login')) {
            setTimeout(() => {
              window.location.href = '/admin/login';
            }, 100);
          }
          
          // Prevent further error propagation
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount or when token changes
    return () => {
      adminAxios.interceptors.response.eject(interceptorId);
    };
  }, [token, adminAxios]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/admin/auth/login`, { email, password });
      
      if (res.data.success) {
        setAdmin(res.data.admin);
        setToken(res.data.token);
        toast.success('Login successful');
        return { success: true };
      } else {
        toast.error(res.data.message || 'Login failed');
        return { success: false, message: res.data.message };
      }
    } catch (e) {
      const message = e.response?.data?.detail || e.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await adminAxios.post('/admin/auth/logout');
      }
    } catch (e) {
      console.warn('Logout API call failed', e);
    } finally {
      setAdmin(null);
      setToken(null);
      localStorage.removeItem(ADMIN_STORAGE_KEY);
      toast.success('Logged out');
    }
  };

  const refreshAdminInfo = async () => {
    if (!token) return;
    
    try {
      const res = await adminAxios.get('/admin/auth/me');
      if (res.data.success) {
        setAdmin(res.data.admin);
      }
    } catch (e) {
      console.warn('Failed to refresh admin info', e);
      // If unauthorized, logout
      if (e.response?.status === 401) {
        logout();
      }
    }
  };

  // Check permission for current admin - exposed as hasPermission for components
  const checkPermission = (resource, action = null) => {
    if (!admin) return false;
    return hasPermission(admin.role, resource, action);
  };

  // Alias for backwards compatibility
  const canAccess = checkPermission;

  return (
    <AdminContext.Provider value={{
      admin,
      token,
      loading,
      isAuthenticated: !!admin && !!token,
      login,
      logout,
      refreshAdminInfo,
      canAccess,
      hasPermission: checkPermission, // Expose as hasPermission for components
      adminAxios,
      BACKEND_URL
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

export default AdminContext;
